import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, fmtEuros, StatusBadge, API } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Landmark, FileDown, Euro, CheckCheck } from "lucide-react";

const TABS = [
  { key: "pending", label: "À comptabiliser" },
  { key: "accounted", label: "Comptabilisées" },
  { key: "paid", label: "Payées" },
  { key: "dons", label: "Transferts au denier (abandons)" },
];

export default function AccountingPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processTarget, setProcessTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [account, setAccount] = useState("");
  const [analytic, setAnalytic] = useState("");
  const [costCenter, setCostCenter] = useState("");
  const [reference, setReference] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/accounting/reports", { params: { tab } })
      .then(({ data }) => setReports(data.reports))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const openProcess = (r) => {
    setProcessTarget(r);
    setAccount(r.accounting?.account || "");
    setAnalytic(r.accounting?.analytic || "");
    setCostCenter(r.accounting?.cost_center || "");
  };

  const submitProcess = async () => {
    if (account.trim().length < 2) return toast.error("Indiquez le compte comptable.");
    setSending(true);
    try {
      await api.post(`/accounting/reports/${processTarget.id}/process`, {
        account: account.trim(), analytic: analytic.trim(), cost_center: costCenter.trim(),
      });
      toast.success(`Note ${processTarget.number} comptabilisée.`);
      setProcessTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const submitPayment = async () => {
    if (reference.trim().length < 2) return toast.error("Indiquez la référence du virement.");
    setSending(true);
    try {
      await api.post(`/accounting/reports/${payTarget.id}/payment`, { reference: reference.trim() });
      toast.success(`Paiement enregistré pour ${payTarget.number}.`);
      setPayTarget(null);
      setReference("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const downloadDossier = async (r) => {
    try {
      const res = await api.get(`/accounting/reports/${r.id}/dossier`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dossier_${r.number}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Dossier ${r.number} téléchargé (justificatifs + historique).`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadExport = async (format) => {
    try {
      const res = await api.get("/accounting/export", { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "xlsx" ? "export_virements.xlsx" : "export_virements.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé (${reports.length} note(s) comptabilisée(s) ou payée(s)).`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6 animate-enter" data-testid="accounting-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "Work Sans" }}>Comptabilité</h1>
          <p className="text-lg text-slate-600 mt-1">Traitement comptable et paiement des notes validées.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => downloadExport("csv")} className="btn-secondary-lg !h-12 !px-5 !text-base" data-testid="export-csv-button">
            <FileDown className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Export CSV
          </button>
          <button onClick={() => downloadExport("xlsx")} className="btn-secondary-lg !h-12 !px-5 !text-base" data-testid="export-xlsx-button">
            <FileDown className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Export XLSX
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            className={`h-12 px-6 rounded-xl font-bold text-base transition-colors ${
              tab === t.key ? "bg-[#0369a1] text-white" : "bg-white text-slate-600 border-2 border-slate-200 hover:border-slate-300"
            }`} data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dons" && (
        <div className="card-base !bg-rose-50 border-2 !border-rose-300" data-testid="dons-banner">
          <p className="text-lg text-rose-900">
            <strong>Notes et dépenses abandonnées = transferts au denier.</strong> Ces montants ne sont pas à rembourser.
            Signalez chaque transfert pour l'établissement éventuel d'un reçu fiscal
            (traitement hors application, au niveau du denier).
          </p>
        </div>
      )}

      {loading ? (
        <p className="text-lg text-slate-600">Chargement…</p>
      ) : reports.length === 0 ? (
        <div className="card-base text-center py-12" data-testid="accounting-empty">
          <Landmark className="h-16 w-16 text-slate-300 mx-auto mb-4" aria-hidden="true" strokeWidth={2.5} />
          <p className="text-xl font-semibold text-slate-700">Aucune note dans cet onglet</p>
        </div>
      ) : (
        <div className="card-base !p-0 overflow-x-auto">
          <table className="w-full text-left" data-testid="accounting-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-4 font-semibold text-slate-700">Note</th>
                <th className="p-4 font-semibold text-slate-700">Demandeur</th>
                <th className="p-4 font-semibold text-slate-700">Période</th>
                <th className="p-4 font-semibold text-slate-700 text-right">Montant validé</th>
                <th className="p-4 font-semibold text-slate-700">Statut</th>
                <th className="p-4 font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`accounting-row-${r.id}`}>
                  <td className="p-4 font-bold text-slate-900">{r.number}</td>
                  <td className="p-4 text-slate-700">{r.requester_name}</td>
                  <td className="p-4 text-slate-700">{r.period}</td>
                  <td className="p-4 text-right font-bold text-slate-900">{fmtEuros(r.total_approved_cents)}</td>
                  <td className="p-4"><StatusBadge status={r.status} label={r.status_label} /></td>
                  <td className="p-4">
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => navigate(`/notes/${r.id}`)}
                        className="h-11 px-4 rounded-lg border-2 border-[#0369a1] text-[#0369a1] font-semibold"
                        data-testid={`view-report-${r.id}`}>
                        Voir
                      </button>
                      <button onClick={() => downloadDossier(r)}
                        className="h-11 px-4 rounded-lg border-2 border-slate-500 text-slate-700 font-semibold flex items-center gap-2"
                        data-testid={`dossier-button-${r.id}`}>
                        <FileDown className="h-4 w-4" aria-hidden="true" /> Dossier
                      </button>
                      {tab === "pending" && (
                        <button onClick={() => openProcess(r)} className="h-11 px-4 rounded-lg bg-[#0369a1] text-white font-semibold flex items-center gap-2"
                          data-testid={`process-button-${r.id}`}>
                          <CheckCheck className="h-4 w-4" aria-hidden="true" /> Comptabiliser
                        </button>
                      )}
                      {tab === "accounted" && (
                        <button onClick={() => setPayTarget(r)} className="h-11 px-4 rounded-lg bg-[#15803d] text-white font-semibold flex items-center gap-2"
                          data-testid={`pay-button-${r.id}`}>
                          <Euro className="h-4 w-4" aria-hidden="true" /> Marquer payée
                        </button>
                      )}
                      {tab === "paid" && r.payment && (
                        <span className="text-slate-600 text-sm">Réf. {r.payment.reference}</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!processTarget} onOpenChange={(o) => !o && setProcessTarget(null)}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="process-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">Comptabiliser {processTarget?.number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-lg text-slate-700">
              {processTarget?.requester_name} — montant validé : <strong>{fmtEuros(processTarget?.total_approved_cents)}</strong>
            </p>
            <div>
              <Label htmlFor="account" className="label-lg">Compte comptable *</Label>
              <Input id="account" value={account} onChange={(e) => setAccount(e.target.value)} className="input-lg"
                placeholder="Ex : 625100" data-testid="accounting-account-input" />
            </div>
            <div>
              <Label htmlFor="analytic" className="label-lg">Axe analytique</Label>
              <Input id="analytic" value={analytic} onChange={(e) => setAnalytic(e.target.value)} className="input-lg"
                placeholder="Ex : PASTORALE" data-testid="accounting-analytic-input" />
            </div>
            <div>
              <Label htmlFor="costcenter" className="label-lg">Centre de coût</Label>
              <Input id="costcenter" value={costCenter} onChange={(e) => setCostCenter(e.target.value)} className="input-lg"
                placeholder="Ex : FOIX" data-testid="accounting-costcenter-input" />
            </div>
            <button onClick={submitProcess} disabled={sending} className="btn-primary-lg w-full" data-testid="process-confirm-button">
              {sending ? "Enregistrement…" : "Enregistrer la comptabilisation"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">Marquer {payTarget?.number} comme payée</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-lg text-slate-700">
              {payTarget?.requester_name} — montant : <strong>{fmtEuros(payTarget?.total_approved_cents)}</strong>
            </p>
            {!payTarget?.requester_iban && (
              <p className="text-amber-700 font-semibold bg-amber-50 border border-amber-200 rounded-lg p-3">
                Attention : aucun IBAN n'est renseigné pour ce demandeur (à compléter dans l'administration).
              </p>
            )}
            <div>
              <Label htmlFor="reference" className="label-lg">Référence du virement *</Label>
              <Input id="reference" value={reference} onChange={(e) => setReference(e.target.value)} className="input-lg"
                placeholder="Ex : VIR-2026-0142" data-testid="payment-reference-input" />
            </div>
            <button onClick={submitPayment} disabled={sending} className="btn-success-lg w-full" data-testid="payment-confirm-button">
              {sending ? "Enregistrement…" : "Confirmer le paiement"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
